import {ApprovalResult} from '@financial/sbill/dist/approvals/types'

import {PaymentPriorityType, SessionStorageKey} from '@web/core/constants'

import {decryptAes, encryptAes, makeSHA1Key} from './crypto'

/**
 * key SHA1 hash
 * value AES cipher
 * Storage 인터페이스
 */
export class SecureStorage<T> {
    private storage: Storage

    private key: string

    private orderSheetId: string

    constructor(storage: Storage, key: string) {
        this.storage = storage
        this.orderSheetId = key
        this.key = makeSHA1Key(key)
    }

    set(value: T) {
        this.storage.setItem(this.key, encryptAes(JSON.stringify(value), this.orderSheetId))
    }

    get(defaultValue: unknown): T {
        const data = this.storage.getItem(this.key)
        if (data) {
            return JSON.parse(decryptAes(data, this.orderSheetId)) as T
        }
        return defaultValue as T
    }

    exists() {
        return !!this.storage.getItem(this.key)
    }

    remove() {
        this.storage.removeItem(this.key)
    }

    clear() {
        this.storage.clear()
    }
}

const storages: {
    [key: string]: unknown
} = {}

const storageFactory = <T>(key: string) => {
    try {
        return new SecureStorage<T>(window.sessionStorage, key)
    } catch {
        // session storage 접근 불가능한 환경. storage 사용 불가 에러 사용처에서 인식할 수 있도록 error 발생시키는 가짜 storage return
        const message = 'Can not access DOMStorage'
        return new SecureStorage<T>(
            {
                clear: () => {
                    throw Error(message)
                },
                getItem: () => {
                    throw Error(message)
                },
                key: (_) => {
                    throw Error(message)
                },
                removeItem: (_) => {
                    throw Error(message)
                },
                setItem: (_, __) => {
                    throw Error(message)
                },
                length: 0,
            },
            key,
        )
    }
}

/**
 * 스토리지 memoize
 */
const getStorage = <T extends object | string>(key: string) => {
    if (!storages[key]) {
        storages[key] = storageFactory<T>(key)
    }
    return storages[key] as SecureStorage<T>
}

export default getStorage

/**
 * 스토리지 존재 여부 확인하기
 */
export const checkStorage = (key: string) => {
    const storage = getStorage(key)

    if (!storage) {
        return false
    }

    return storage.exists
}

/**
 * 스토리지 저장하기
 */
export const saveStorage = <T extends object | string>(key: string, storageValue: T) => {
    const storage = getStorage(key)

    if (!storage) {
        return
    }

    storage.set(storageValue)
}

/**
 * 스토리지 비우기
 */
export const removeStorage = (key: string) => {
    const storage = getStorage(key)

    if (!storage) {
        return
    }

    storage.remove()
}

export type FdsStorageInfoType = {
    targetUrl: string
    sessionInfo: {
        orderProcessId: string
        orderSessionIoAccessUrl: string
        orderSessionIoChannel: string
        applySessionIoAccessUrl: string
        applySessionIoChannel: string
    }
    fdsResult: boolean | undefined
    apply?: boolean
    sBillAuthResult?: ApprovalResult
}

/**
 * FDS 정보가 기록된 Storage 조회
 */
export const getFdsStorageInfo = () => {
    const fdsStorage = getStorage<FdsStorageInfoType>(SessionStorageKey.FDS_SESSION_STORAGE)
    const fdsInfo = fdsStorage.get(SessionStorageKey.FDS_SESSION_STORAGE)

    return {fdsStorage, fdsInfo}
}

type AfterRenewPaymentsValueObject = {paymentType?: PaymentPriorityType}

/**
 * 복원 type이 담긴 storage
 */
export const getRenewPaymentsStorage = () => {
    return getStorage<AfterRenewPaymentsValueObject>('afterRenewPaymentsRequest')
}
